import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { Alert, Drawer, Form, Input, Modal, Table } from "antd";
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  SectionCard,
  StatusTag,
  TabBar,
  fmtDateTime,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import notify from "../utils/notify";

const { TextArea } = Input;

const MaintenancePage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [apiErrors, setApiErrors] = useState(null);
  const [statusTab, setStatusTab] = useState("all");
  const [searchText, setSearchText] = useState("");

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/Maintenance/MyTasks?hotelId=${authData.hotelId}&userIdOrEmail=${authData.userId || authData.userName}`
      );
      const tasksData = Array.isArray(response.data) ? response.data : [];
      setTasks(
        tasksData.map((task) => ({
          ...task,
          key: task.maintenanceTaskId,
          assignedTo: task.assignedTo || {
            userId: task.assignedToUserId,
            userName: task.assignedUserName || "Unknown"
          }
        }))
      );
      setApiErrors(null);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setApiErrors({
        status: err.response?.status,
        data: err.response?.data,
        config: err.config
      });
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to load maintenance tasks"
      });
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authData?.hotelId && (authData?.userId || authData?.userName)) {
      fetchTasks();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the listed values change; the function is re-created every render and is not a trigger
  }, [authData?.hotelId, authData?.userId, authData?.userName]);

  const statusLabel = (s) => (s === "InProgress" ? "In progress" : s || "Unknown");
  const renderStatusTag = (status) => <StatusTag status={status}>{statusLabel(status)}</StatusTag>;
  const renderPriorityTag = (priority) => <StatusTag status={priority}>{priority || "Unknown"}</StatusTag>;

  const openComplete = (record) => {
    setCurrentTask(record);
    setApiErrors(null);
    form.resetFields();
    setCompleteModalOpen(true);
  };

  const openView = (record) => {
    setCurrentTask(record);
    setViewModalOpen(true);
  };

  const handleCompleteTask = async () => {
  try {
    const values = await form.validateFields();
    const updatePayload = {
      status: "Completed",
      description: values.notes || currentTask.description,
      completedDate: new Date().toISOString(),
      title: currentTask.title,
      roomId: currentTask.roomId,
      location: currentTask.location,
      priority: currentTask.priority,
      assignmentNotes: currentTask.assignmentNotes,
      hotelId: currentTask.hotelId,
      assignedToUserId: currentTask.assignedToUserId ? parseInt(currentTask.assignedToUserId) : null
    };

    await axiosInstance.put(
      `/Maintenance/${currentTask.maintenanceTaskId}?hotelId=${currentTask.hotelId}`,
      updatePayload
    );

    notify.success({
      title: "Success",
      description: "Task marked as completed"
    });

    setCompleteModalOpen(false);
    setApiErrors(null);
    fetchTasks();
  } catch (err) {
    console.error("Error completing task:", err);
    setApiErrors({
      status: err.response?.status,
      data: err.response?.data,
      config: err.config
    });
    notify.error({
      title: "Error completing task",
      description:
        err.response?.data?.title ||
        err.response?.data?.message ||
        "Please check your input and try again"
    });
  }
};


  const counts = useMemo(() => {
    const c = { Pending: 0, InProgress: 0, Completed: 0, Cancelled: 0 };
    tasks.forEach((x) => {
      if (c[x.status] != null) c[x.status] += 1;
    });
    return c;
  }, [tasks]);

  const tabs = [
    { key: "all", label: "All", count: tasks.length },
    { key: "Pending", label: "Pending", count: counts.Pending },
    { key: "InProgress", label: "In progress", count: counts.InProgress },
    { key: "Completed", label: "Completed", count: counts.Completed },
    { key: "Cancelled", label: "Cancelled", count: counts.Cancelled },
  ];

  const visibleTasks = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return tasks.filter((x) => {
      if (statusTab !== "all" && x.status !== statusTab) return false;
      if (!q) return true;
      return `${x.title || ""} ${x.location || ""} ${x.roomNumber || ""} ${x.description || ""}`.toLowerCase().includes(q);
    });
  }, [tasks, statusTab, searchText]);

  const roomLabel = (record) => record.roomNumber || (record.roomId ? `Room ID ${record.roomId}` : record.location || "—");

  const columns = [
    {
      title: "Task",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{text}</span>
          <span className="hms-cell-sub">#{record.maintenanceTaskId}</span>
        </div>
      ),
    },
    {
      title: "Room",
      key: "room",
      width: 150,
      render: (_, record) => roomLabel(record),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (text) => text || <span className="hms-muted">—</span>,
    },
    { title: "Status", dataIndex: "status", key: "status", width: 140, render: renderStatusTag },
    { title: "Priority", dataIndex: "priority", key: "priority", width: 130, render: renderPriorityTag },
    {
      title: "",
      key: "action",
      fixed: "right",
      align: "right",
      width: 170,
      render: (_, record) => (
        <span onClick={(e) => e.stopPropagation()}>
          <RowActions
            actions={[
              {
                key: "complete",
                label: "Complete",
                icon: "accept",
                showLabel: true,
                tone: "primary",
                hidden: record.status === "Completed" || record.status === "Cancelled",
                keepSpace: false,
                onClick: () => openComplete(record),
              },
              { key: "view", label: "View details", icon: "view", onClick: () => openView(record) },
            ]}
          />
        </span>
      ),
    },
  ];

  return (
    <div className="hms-page mt-page">
      <PageHeader />

      <SectionCard
        flush
        title={<CardTitle count={tasks.length}>My maintenance tasks</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: searchText, onChange: setSearchText, placeholder: "Search tasks" }}
            onRefresh={fetchTasks}
            loading={loading}
          />
        }
      >
        <TabBar tabs={tabs} value={statusTab} onChange={setStatusTab} />

        <Table
          className="hms-table"
          rowKey="maintenanceTaskId"
          columns={columns}
          dataSource={visibleTasks}
          loading={loading}
          pagination={tablePagination("task")}
          scroll={{ x: 900 }}
          onRow={(record) => ({ onClick: () => openView(record), style: { cursor: "pointer" } })}
          locale={{
            emptyText: (
              <div className="hms-empty">
                {tasks.length === 0 ? "You have no maintenance tasks assigned to you." : "No tasks in this view"}
              </div>
            ),
          }}
        />
      </SectionCard>

      <Modal
        title={`Complete task #${currentTask?.maintenanceTaskId || ""}`}
        open={completeModalOpen}
        onCancel={() => setCompleteModalOpen(false)}
        onOk={handleCompleteTask}
        okText="Mark as completed"
        width={520}
        destroyOnHidden
      >
        {apiErrors && (
          <Alert
            title="Validation error"
            description={
              typeof apiErrors?.data?.errors === "object" ? (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {Object.entries(apiErrors.data.errors).map(([field, messages]) => (
                    <li key={field}>
                      <strong>{field}:</strong> {Array.isArray(messages) ? messages.join(", ") : messages}
                    </li>
                  ))}
                </ul>
              ) : (
                apiErrors?.data?.errors || JSON.stringify(apiErrors?.data)
              )
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form form={form} layout="vertical">
          <Form.Item
            name="notes"
            label="Completion notes"
            rules={[{ required: true, message: "Please enter completion notes" }]}
          >
            <TextArea rows={4} placeholder="Describe the work completed" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer title="Task details" open={viewModalOpen} onClose={() => setViewModalOpen(false)} size={560} destroyOnHidden>
        {currentTask && (
          <>
            <div className="hms-summary">
              <div>
                <div className="hms-summary-title">{currentTask.title}</div>
                <div className="hms-summary-sub">Task #{currentTask.maintenanceTaskId}</div>
              </div>
              {renderStatusTag(currentTask.status)}
            </div>

            <DetailList
              items={[
                { label: "Priority", value: renderPriorityTag(currentTask.priority) },
                { label: "Description", value: currentTask.description },
                { label: "Room", value: roomLabel(currentTask) },
                { label: "Location", value: currentTask.location },
                {
                  label: "Assigned to",
                  value: currentTask.assignedTo
                    ? `${currentTask.assignedTo.userName}${currentTask.assignedTo.role ? ` (${currentTask.assignedTo.role})` : ""}`
                    : "Not assigned",
                },
                { label: "Created", value: currentTask.createdDate ? fmtDateTime(currentTask.createdDate) : null },
                { label: "Completed", value: currentTask.completedDate ? fmtDateTime(currentTask.completedDate) : null },
                { label: "Assignment notes", value: currentTask.assignmentNotes },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default MaintenancePage;
